import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLeaveInit,
  useSubstitutes,
  useCalculateHours,
  useCreateLeave,
  useSubmitLeave,
} from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';
import type { LeaveType, LeaveBalance, LeaveApplyRequest } from '@saiens/shared/types';

function getTodayStr(): string {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

export function LeaveApplyPage() {
  const navigate = useNavigate();
  const { data: initData, isLoading: initLoading } = useLeaveInit();
  const { data: substitutes } = useSubstitutes();
  const calculateHours = useCalculateHours();
  const createLeave = useCreateLeave();
  const submitLeave = useSubmitLeave();

  const [holidayTypeId, setHolidayTypeId] = useState<number | ''>('');
  const [startDay, setStartDay] = useState(getTodayStr());
  const [endDay, setEndDay] = useState(getTodayStr());
  const [hourFrom, setHourFrom] = useState<number | ''>('');
  const [minFrom, setMinFrom] = useState<number | ''>('');
  const [hourTo, setHourTo] = useState<number | ''>('');
  const [minTo, setMinTo] = useState<number | ''>('');
  const [substituteId, setSubstituteId] = useState<number | ''>('');
  const [reportNote, setReportNote] = useState('');
  const [calculatedHours, setCalculatedHours] = useState<number | null>(null);
  const [calculatedDisplay, setCalculatedDisplay] = useState('');

  const selectedType: LeaveType | undefined = useMemo(
    () => initData?.types.find((t) => t.id === holidayTypeId),
    [initData?.types, holidayTypeId]
  );

  const isTimeType = selectedType && selectedType.request_unit !== 'day';

  // Find matching balance for selected type
  const selectedBalance: LeaveBalance | undefined = useMemo(
    () => initData?.balance.find((b) => b.type_id === holidayTypeId),
    [initData?.balance, holidayTypeId]
  );

  // Set default time values when init data loads
  useEffect(() => {
    if (initData?.hour_options && initData.hour_options.length > 0 && hourFrom === '') {
      const nine = initData.hour_options.find((h) => h.value === 9);
      const eighteen = initData.hour_options.find((h) => h.value === 18);
      if (nine) setHourFrom(nine.value);
      if (eighteen) setHourTo(eighteen.value);
    }
    if (initData?.minute_options && initData.minute_options.length > 0 && minFrom === '') {
      const zero = initData.minute_options.find((m) => m.value === 0);
      if (zero) {
        setMinFrom(zero.value);
        setMinTo(zero.value);
      }
    }
  }, [initData, hourFrom, minFrom]);

  // Debounced calculate hours when relevant fields change
  const calcTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!holidayTypeId || !startDay || !endDay) {
      setCalculatedHours(null);
      setCalculatedDisplay('');
      return;
    }

    clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      const req = {
        holiday_type_id: Number(holidayTypeId),
        start_day: startDay,
        end_day: endDay,
        ...(isTimeType && hourFrom !== '' && { hour_from: Number(hourFrom) }),
        ...(isTimeType && minFrom !== '' && { min_from: Number(minFrom) }),
        ...(isTimeType && hourTo !== '' && { hour_to: Number(hourTo) }),
        ...(isTimeType && minTo !== '' && { min_to: Number(minTo) }),
      };

      calculateHours.mutate(req, {
        onSuccess: (result) => {
          setCalculatedHours(result.hours);
          setCalculatedDisplay(result.display_time);
        },
      });
    }, 500);

    return () => clearTimeout(calcTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holidayTypeId, startDay, endDay, hourFrom, minFrom, hourTo, minTo, isTimeType]);

  const buildPayload = useCallback((): LeaveApplyRequest => ({
    holiday_type_id: Number(holidayTypeId),
    start_day: startDay,
    end_day: endDay,
    substitute_id: Number(substituteId),
    report_note: reportNote,
    ...(isTimeType && hourFrom !== '' && { hour_from: Number(hourFrom) }),
    ...(isTimeType && minFrom !== '' && { min_from: Number(minFrom) }),
    ...(isTimeType && hourTo !== '' && { hour_to: Number(hourTo) }),
    ...(isTimeType && minTo !== '' && { min_to: Number(minTo) }),
  }), [holidayTypeId, startDay, endDay, substituteId, reportNote, isTimeType, hourFrom, minFrom, hourTo, minTo]);

  const isSaving = createLeave.isPending || submitLeave.isPending;

  const canSubmit =
    holidayTypeId !== '' &&
    startDay &&
    endDay &&
    substituteId !== '' &&
    !isSaving;

  const handleSaveDraft = useCallback(() => {
    if (!canSubmit) return;
    createLeave.mutate(buildPayload(), {
      onSuccess: (result) => {
        navigate(`/leave/${result.id}`, { replace: true });
      },
    });
  }, [canSubmit, buildPayload, createLeave, navigate]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    createLeave.mutate(buildPayload(), {
      onSuccess: (result) => {
        submitLeave.mutate(result.id, {
          onSuccess: () => {
            navigate('/leave', { replace: true });
          },
        });
      },
    });
  }, [canSubmit, buildPayload, createLeave, submitLeave, navigate]);

  if (initLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">請假申請</h1>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Leave Type */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">假別類型</label>
          <select
            value={holidayTypeId}
            onChange={(e) => setHolidayTypeId(e.target.value ? Number(e.target.value) : '')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
          >
            <option value="">請選擇假別</option>
            {initData?.types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.request_unit_label})
              </option>
            ))}
          </select>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">開始日</label>
            <input
              type="date"
              value={startDay}
              onChange={(e) => setStartDay(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">結束日</label>
            <input
              type="date"
              value={endDay}
              onChange={(e) => setEndDay(e.target.value)}
              min={startDay}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
            />
          </div>
        </div>

        {/* Time Selection (only for non-day types) */}
        {isTimeType && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">開始時間</label>
                <div className="flex gap-1">
                  <select
                    value={hourFrom}
                    onChange={(e) => setHourFrom(e.target.value ? Number(e.target.value) : '')}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-pink-500 focus:outline-none"
                  >
                    {initData?.hour_options.map((h) => (
                      <option key={h.id} value={h.value}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={minFrom}
                    onChange={(e) => setMinFrom(e.target.value ? Number(e.target.value) : '')}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-pink-500 focus:outline-none"
                  >
                    {initData?.minute_options.map((m) => (
                      <option key={m.id} value={m.value}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">結束時間</label>
                <div className="flex gap-1">
                  <select
                    value={hourTo}
                    onChange={(e) => setHourTo(e.target.value ? Number(e.target.value) : '')}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-pink-500 focus:outline-none"
                  >
                    {initData?.hour_options.map((h) => (
                      <option key={h.id} value={h.value}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={minTo}
                    onChange={(e) => setMinTo(e.target.value ? Number(e.target.value) : '')}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-pink-500 focus:outline-none"
                  >
                    {initData?.minute_options.map((m) => (
                      <option key={m.id} value={m.value}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculated Hours & Balance */}
        {calculatedHours != null && (
          <div className="flex gap-3">
            <div className="flex-1 rounded-lg bg-pink-50 p-3">
              <p className="text-xs text-pink-600">預計時數</p>
              <p className="text-lg font-bold text-pink-700">
                {calculatedDisplay || formatWorkHours(calculatedHours)}
              </p>
            </div>
            {selectedBalance && (
              <div className="flex-1 rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">剩餘額度</p>
                <p className="text-lg font-bold text-gray-700">
                  {formatWorkHours(selectedBalance.remaining_hours)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Substitute */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">代理人</label>
          <select
            value={substituteId}
            onChange={(e) => setSubstituteId(e.target.value ? Number(e.target.value) : '')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
          >
            <option value="">請選擇代理人</option>
            {substitutes?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.department ? ` (${s.department})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Reason */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">請假事由</label>
          <textarea
            value={reportNote}
            onChange={(e) => setReportNote(e.target.value)}
            rows={3}
            placeholder="請輸入請假事由"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-4 pt-2">
          <button
            onClick={handleSaveDraft}
            disabled={!canSubmit}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {createLeave.isPending ? '儲存中...' : '儲存草稿'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-pink-500 py-3 text-sm font-medium text-white hover:bg-pink-600 disabled:opacity-40"
          >
            {submitLeave.isPending ? '送出中...' : '送出申請'}
          </button>
        </div>
      </div>
    </div>
  );
}
